import * as React from 'react';
import { useCallback } from 'react';

import { Pressable } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';
import { Icon } from 'ucl/components/icon/Icon';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { Box, Typography } from 'components/ui';
import { A11yWrapper } from 'components/ui/a11y-wrapper';
import RadioIndicator from 'components/ui/radio-indicator';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { primitive, spacing } from 'styles/constants';
import { isNative } from 'utils/environment';
import { TraceName, stopTrace } from 'utils/performance/trace';

import { StoreOrderButton } from '../store-actions/components';

import { StoreCardActions, StoreCardDetails } from './components';
import { getOrderHereButtonClickSource } from './order-here-button-click-source';
import { StoreCardViewProps } from './types';

export const StoreCardView: React.FC<StoreCardViewProps> = ({
  tab,
  isFavorite = false,
  isUpdatingFavorites = false,
  onCardPress,
  onDiagnosticsClick,
  onFavoriteClick,
  onOrderPress,
  onInfoPress,
  storeDetails,
  errorMessage,
  isInMapView,
  isInSupportForm,
  isOffersSelection,
  isSelected,
  isLastItem,
  ineligibleStoreMessage,
}) => {
  const {
    address,
    distanceText,
    restaurant,
    restaurantStatus: { canBeSelected, text: storeStatusText },
    isHalal,
    city,
    accessInstructions,
  } = storeDetails;
  const { modalAuthState } = useAuthContext();

  const isFavoriteSearch = modalAuthState.screen === ModalAuthScreen.SIGN_UP;
  const halalTitle = isHalal ? t('isHalalTitle') : '';
  const accessibilityLabel = `${address} ${city} ${storeStatusText}, ${halalTitle}, ${accessInstructions}`;

  const handleOnPress = () => {
    if (!isSelected) {
      onCardPress?.();
    }
  };

  const onLayout = useCallback(() => {
    if (tab === OrderLocatorTab.NEARBY) {
      // the 1st store that hits will stop the trace, any more don't matter as trace is done.
      stopTrace(TraceName.STORE_LOCATOR_AUTO_NEARBY_LOCATION_SEARCH_TO_LIST_ITEM_DISPLAYED);
      stopTrace(TraceName.STORE_LOCATOR_RESTAURANT_ALREADY_SELECTED_TO_LIST_ITEM_DISPLAYED);
      stopTrace(TraceName.STORE_LOCATOR_MANUAL_SEARCH_LOCATION_PRESSED_TO_LIST_ITEM_DISPLAYED);
    }
  }, [tab]);

  if (isOffersSelection) {
    return (
      <Box
        onLayout={onLayout}
        style={[
          styles.container,
          !canBeSelected && styles.unavailableContainer,
          isNative && isLastItem && styles.lastItemContainer,
        ]}
      >
        <Pressable
          onPress={canBeSelected ? onCardPress : undefined}
          accessible
          accessibilityState={{ selected: !!isSelected, disabled: !canBeSelected }}
          accessibilityRole="radio"
          accessibilityLabel={accessibilityLabel}
          dd-action-name={`${storeDetails.address} select restaurant for offers`}
          disabled={!canBeSelected}
        >
          <Box style={styles.offersRow}>
            <StoreCardDetails
              errorMessage={errorMessage}
              storeDetails={storeDetails}
              isInMapView={isInMapView}
              hideDistanceInDetails
              // Design: pinned ineligible store shows address + distance only.
              addressOnly={!canBeSelected}
            />
            <Box style={styles.distanceWrapper}>
              <A11yWrapper style={styles.distanceA11yWrapper}>
                <Typography fontFamily="heading" focusable>
                  {distanceText}
                </Typography>
              </A11yWrapper>
            </Box>
            <RadioIndicator isSelected={!!isSelected} isDisabled={!canBeSelected} />
          </Box>
          {!canBeSelected ? (
            <Box style={styles.offerUnavailableBanner} testID="offer-unavailable-in-restaurant">
              <Icon variant="info" size="4" color={primitive.$bbqBrown} />
              <Typography variant="copyTwo" color={primitive.$bbqBrown} style={styles.bannerText}>
                {ineligibleStoreMessage ?? t('offerNotAvailableInThisRestaurant')}
              </Typography>
            </Box>
          ) : null}
        </Pressable>
      </Box>
    );
  }

  return (
    <Box
      onLayout={onLayout}
      style={[
        styles.container,
        !canBeSelected && styles.unavailableContainer,
        isNative && isLastItem && styles.lastItemContainer,
      ]}
    >
      <Pressable
        onPress={handleOnPress}
        accessible={!isSelected}
        accessibilityState={{ expanded: !!isSelected }}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        dd-action-name={`${storeDetails.address} open accordion`}
        aria-expanded={isSelected}
      >
        <Box style={styles.row}>
          <StoreCardDetails
            errorMessage={errorMessage}
            storeDetails={storeDetails}
            isInMapView={isInMapView}
            distanceComponent={
              <Box style={styles.distanceWrapper}>
                <A11yWrapper style={styles.distanceA11yWrapper}>
                  <Typography fontFamily="heading" focusable>
                    {distanceText}
                  </Typography>
                </A11yWrapper>
              </Box>
            }
          />
        </Box>
      </Pressable>
      <Box style={styles.row}>
        {(isSelected || isInMapView) && (
          <StoreCardActions
            dd-action-name={`${address} ${isFavorite ? 'unfavorite' : 'favorite'}`}
            isFavorite={isFavorite}
            canBeSelected={canBeSelected}
            onDiagnosticsClick={!isFavoriteSearch ? onDiagnosticsClick : undefined}
            onFavoritesClick={!isFavoriteSearch ? onFavoriteClick : onCardPress}
            onInfoPress={onInfoPress}
            isUpdatingFavorites={isUpdatingFavorites}
            containerStyle={{
              flex: onDiagnosticsClick ? 2 : 1,
              paddingTop: spacing.$7,
              justifyContent: 'space-around',
              paddingRight: spacing.$6,
            }}
          />
        )}
        <Box style={styles.orderButtonWrapper}>
          {isSelected && onInfoPress && !canBeSelected && (
            <ActionButton
              fullWidth
              variant={ActionButtonVariants.OUTLINE}
              onPress={onInfoPress}
              alignSelf={'center'}
              marginTop={'$5'}
              borderColor={primitive.$bbqBrown}
              _text={{ color: primitive.$bbqBrown }}
            >
              {t('viewStoreHours')}
            </ActionButton>
          )}
          {(isSelected || isInMapView) && canBeSelected && (
            <StoreOrderButton
              isInSupportForm={isInSupportForm}
              onOrderPress={onOrderPress}
              orderHereSource={getOrderHereButtonClickSource({ tab, isInSupportForm })}
              restaurant={restaurant}
              disabled={!canBeSelected}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    padding: spacing.$4,
  },
  distanceA11yWrapper: {
    flexGrow: 0,
  },
  distanceWrapper: {
    marginRight: spacing.$3,
  },
  lastItemContainer: {
    paddingBottom: spacing.$7,
  },
  offersRow: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  offerUnavailableBanner: {
    alignItems: 'center',
    backgroundColor: '#F8E8E4',
    borderCurve: 'continuous',
    borderRadius: 8,
    flexDirection: 'row',
    gap: spacing.$2,
    marginTop: spacing.$3,
    paddingHorizontal: spacing.$3,
    paddingVertical: spacing.$2,
  },
  bannerText: {
    flex: 1,
    fontSize: 13,
  },
  orderButtonWrapper: {
    flex: 5,
  },
  row: {
    flexDirection: 'row',
  },
  unavailableContainer: {
    backgroundColor: primitive.$blackOpacity4,
    borderCurve: 'continuous',
    borderRadius: 12,
  },
});
